// Painel lateral do editor: propriedades da camada selecionada, faixas,
// area da foto e botoes para adicionar camadas.

import type { ReactNode } from "react";
import { Plus, Trash2 } from "lucide-react";
import {
  createWatermarkLayer,
  BAND_SIZE_MAX,
  BAND_SIZE_MIN,
  MAX_WATERMARK_LAYERS,
  MAX_WATERMARK_TEXT,
  WATERMARK_LAYER_KINDS,
  type WatermarkBand,
  type WatermarkDesign,
  type WatermarkLayer,
} from "@lucralink/shared";
import { Input } from "@/components/ui/input";
import { Segmented, Toggle } from "@/components/forwarding/controls";
import { LAYER_LABELS, nextLayerId, removeLayer, updateLayer } from "@/lib/watermark";

interface InspectorProps {
  readonly design: WatermarkDesign;
  readonly selectedId: string | null;
  readonly onSelect: (id: string | null) => void;
  readonly onChange: (design: WatermarkDesign) => void;
}

const VARIABLES = ["{title}", "{price}", "{originalPrice}", "{discountPercent}", "{coupon}"];

function Field({
  label,
  children,
}: {
  readonly label: string;
  readonly children: ReactNode;
}): JSX.Element {
  return (
    <label className="flex flex-col gap-1 text-xs font-medium text-muted-foreground">
      {label}
      {children}
    </label>
  );
}

function ColorField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}): JSX.Element {
  return (
    <Field label={label}>
      <span className="flex items-center gap-2">
        <input
          type="color"
          aria-label={label}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-8 w-10 cursor-pointer rounded border border-border bg-transparent"
        />
        <span className="font-mono text-[11px] text-muted-foreground">{value.toUpperCase()}</span>
      </span>
    </Field>
  );
}

function TextField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}): JSX.Element {
  return (
    <Field label={label}>
      <Input
        value={value}
        maxLength={MAX_WATERMARK_TEXT}
        onChange={(e) => onChange(e.target.value)}
      />
      <span className="flex flex-wrap gap-1">
        {VARIABLES.map((v) => (
          <button
            key={v}
            type="button"
            onClick={() => onChange(`${value}${value ? " " : ""}${v}`)}
            className="rounded border border-border px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground hover:border-primary hover:text-foreground"
          >
            {v}
          </button>
        ))}
      </span>
    </Field>
  );
}

function RangeField({
  label,
  value,
  min,
  max,
  step,
  onChange,
  format,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (v: number) => void;
  format: (v: number) => string;
}): JSX.Element {
  return (
    <Field label={`${label}: ${format(value)}`}>
      <input
        type="range"
        aria-label={label}
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="accent-[hsl(var(--primary))]"
      />
    </Field>
  );
}

function BackgroundField({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (v: string | null) => void;
}): JSX.Element {
  return (
    <div className="space-y-2">
      <Toggle
        id="layer-bg"
        checked={value !== null}
        onChange={(on) => onChange(on ? "#111827" : null)}
        label="Fundo atrás do texto"
      />
      {value !== null && <ColorField label="Cor do fundo" value={value} onChange={onChange} />}
    </div>
  );
}

type LayerPatch = (patch: Partial<WatermarkLayer>) => void;
type Of<K extends WatermarkLayer["kind"]> = Extract<WatermarkLayer, { kind: K }>;

const SHAPES = [
  { value: "circle", label: "Círculo" },
  { value: "pill", label: "Pílula" },
  { value: "ribbon", label: "Fita" },
] as const;
const ALIGN_OPTIONS = [
  { value: "left", label: "Esquerda" },
  { value: "center", label: "Centro" },
  { value: "right", label: "Direita" },
] as const;
const percent = (v: number) => `${Math.round(v * 100)}%`;

function RotationField({ layer, set }: { layer: WatermarkLayer; set: LayerPatch }): JSX.Element {
  return (
    <RangeField
      label="Inclinação"
      value={layer.rotation}
      min={-45}
      max={45}
      step={1}
      onChange={(v) => set({ rotation: v })}
      format={(v) => `${v}°`}
    />
  );
}

function BadgeFields({ layer, set }: { layer: Of<"badge">; set: LayerPatch }): JSX.Element {
  return (
    <>
      <TextField label="Texto do selo" value={layer.text} onChange={(text) => set({ text })} />
      <Segmented
        label="Formato"
        value={layer.shape}
        onChange={(shape) => set({ shape })}
        options={SHAPES}
      />
      <ColorField label="Cor do selo" value={layer.color} onChange={(color) => set({ color })} />
      <ColorField
        label="Cor do texto"
        value={layer.textColor}
        onChange={(textColor) => set({ textColor })}
      />
    </>
  );
}

function TextFields({
  layer,
  set,
}: {
  layer: Of<"text"> | Of<"contacts">;
  set: LayerPatch;
}): JSX.Element {
  return (
    <>
      <TextField label="Texto" value={layer.text} onChange={(text) => set({ text })} />
      <ColorField label="Cor do texto" value={layer.color} onChange={(color) => set({ color })} />
      <BackgroundField value={layer.background} onChange={(background) => set({ background })} />
      {layer.kind === "text" && (
        <>
          <Segmented
            label="Alinhamento"
            value={layer.align}
            onChange={(align) => set({ align })}
            options={ALIGN_OPTIONS}
          />
          <Toggle
            id="layer-bold"
            checked={layer.bold}
            onChange={(bold) => set({ bold })}
            label="Negrito"
          />
        </>
      )}
    </>
  );
}

function LayerFields({ layer, set }: { layer: WatermarkLayer; set: LayerPatch }): JSX.Element {
  return (
    <>
      {layer.kind === "logo" && (
        <RangeField
          label="Opacidade"
          value={layer.opacity}
          min={0.1}
          max={1}
          step={0.05}
          onChange={(v) => set({ opacity: v })}
          format={percent}
        />
      )}
      {layer.kind === "badge" && <BadgeFields layer={layer} set={set} />}
      {(layer.kind === "text" || layer.kind === "contacts") && (
        <TextFields layer={layer} set={set} />
      )}
      <RotationField layer={layer} set={set} />
    </>
  );
}

function BandFields({
  title,
  band,
  onChange,
}: {
  title: string;
  band: WatermarkBand;
  onChange: (band: WatermarkBand) => void;
}): JSX.Element {
  const set = (patch: Partial<WatermarkBand>) => onChange({ ...band, ...patch });
  return (
    <div className="space-y-3 rounded-md border border-border p-3">
      <Toggle
        id={title === "Faixa superior" ? "band-top" : "band-bottom"}
        checked={band.enabled}
        onChange={(enabled) => set({ enabled })}
        label={title}
      />
      {band.enabled && (
        <>
          <TextField label="Texto da faixa" value={band.text} onChange={(text) => set({ text })} />
          <div className="grid grid-cols-2 gap-3">
            <ColorField
              label="Cor da faixa"
              value={band.color}
              onChange={(color) => set({ color })}
            />
            <ColorField
              label="Cor do texto"
              value={band.textColor}
              onChange={(textColor) => set({ textColor })}
            />
          </div>
          <RangeField
            label="Altura"
            value={band.size}
            min={BAND_SIZE_MIN}
            max={BAND_SIZE_MAX}
            step={0.01}
            onChange={(size) => set({ size })}
            format={(v) => `${Math.round(v * 100)}%`}
          />
        </>
      )}
    </div>
  );
}

function AddLayerBar({
  design,
  onAdd,
}: {
  design: WatermarkDesign;
  onAdd: (kind: WatermarkLayer["kind"]) => void;
}): JSX.Element {
  const canAdd = design.layers.length < MAX_WATERMARK_LAYERS;
  return (
    <div className="space-y-2">
      <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
        Camadas
      </p>
      <div className="flex flex-wrap gap-1.5">
        {WATERMARK_LAYER_KINDS.map((kind) => (
          <button
            key={kind}
            type="button"
            disabled={!canAdd}
            onClick={() => onAdd(kind)}
            className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs hover:border-primary disabled:opacity-40"
          >
            <Plus size={12} /> {LAYER_LABELS[kind]}
          </button>
        ))}
      </div>
      {!canAdd && (
        <p className="text-[11px] text-muted-foreground">
          Limite de {MAX_WATERMARK_LAYERS} camadas.
        </p>
      )}
    </div>
  );
}

function SelectedLayer({
  layer,
  onRemove,
  set,
}: {
  layer: WatermarkLayer | null;
  onRemove: () => void;
  set: LayerPatch;
}): JSX.Element {
  if (!layer) {
    return (
      <p className="rounded-md border border-dashed border-border p-3 text-xs text-muted-foreground">
        Clique numa camada da prévia para editar. Arraste para mover e use o canto para
        redimensionar.
      </p>
    );
  }
  return (
    <div className="space-y-3 rounded-md border border-primary/30 bg-primary/5 p-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium">{LAYER_LABELS[layer.kind]}</p>
        <button
          type="button"
          onClick={onRemove}
          className="inline-flex items-center gap-1 text-xs text-destructive hover:underline"
        >
          <Trash2 size={12} /> Remover
        </button>
      </div>
      <LayerFields layer={layer} set={set} />
    </div>
  );
}

function PhotoFields({
  design,
  onChange,
}: {
  design: WatermarkDesign;
  onChange: (design: WatermarkDesign) => void;
}): JSX.Element {
  const setPhoto = (photo: Partial<WatermarkDesign["photo"]>) =>
    onChange({ ...design, photo: { ...design.photo, ...photo } });
  return (
    <div className="space-y-3 rounded-md border border-border p-3">
      <p className="text-sm font-medium">Área da foto</p>
      <Segmented
        label="Encaixe da foto"
        value={design.photo.fit}
        onChange={(fit) => setPhoto({ fit })}
        options={[
          { value: "contain", label: "Caber inteira" },
          { value: "cover", label: "Preencher" },
        ]}
      />
      <ColorField
        label="Cor de fundo"
        value={design.photo.background}
        onChange={(background) => setPhoto({ background })}
      />
    </div>
  );
}

export function WatermarkInspector({
  design,
  selectedId,
  onSelect,
  onChange,
}: InspectorProps): JSX.Element {
  const layer = design.layers.find((l) => l.id === selectedId) ?? null;
  const addLayer = (kind: WatermarkLayer["kind"]) => {
    const id = nextLayerId(design, kind);
    onChange({ ...design, layers: [...design.layers, createWatermarkLayer(kind, id)] });
    onSelect(id);
  };
  const removeSelected = () => {
    if (layer) onChange(removeLayer(design, layer.id));
    onSelect(null);
  };
  const setLayer: LayerPatch = (patch) => {
    if (layer)
      onChange(updateLayer(design, layer.id, (l) => ({ ...l, ...patch }) as WatermarkLayer));
  };
  return (
    <div className="space-y-4">
      <AddLayerBar design={design} onAdd={addLayer} />
      <SelectedLayer layer={layer} onRemove={removeSelected} set={setLayer} />
      <BandFields
        title="Faixa superior"
        band={design.topBand}
        onChange={(topBand) => onChange({ ...design, topBand })}
      />
      <BandFields
        title="Faixa inferior"
        band={design.bottomBand}
        onChange={(bottomBand) => onChange({ ...design, bottomBand })}
      />
      <PhotoFields design={design} onChange={onChange} />
    </div>
  );
}
